import { forwardRef } from "react";
import type { HTMLAttributes } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "@/lib/auth";
import Badge from "@/components/shared/ui/Badge";
import Button from "@/components/shared/ui/Button";

interface NavbarProps extends HTMLAttributes<HTMLElement> {
  user: {
    name: string;
    email: string;
    user_type: "investor" | "client";
  };
}

const Navbar = forwardRef<HTMLElement, NavbarProps>(
  ({ user, className = "", ...props }, ref) => {
    const navigate = useNavigate();

    const handleSignOut = async () => {
      try {
        await signOut();
        navigate("/");
      } catch (error) {
        console.error("Error signing out:", error);
      }
    };

    return (
      <nav
        ref={ref}
        className={`bg-black border-b-2 border-white ${className}`}
        style={{ fontFamily: "'Courier New', monospace" }}
        {...props}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left - Logo */}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black text-white tracking-[0.2em]">
                TCGL
              </h1>
              <span className="text-xs text-gray-400 uppercase tracking-widest">
                Ventures
              </span>
            </div>

            {/* Right - User Info */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* User Details */}
              <div className="hidden md:flex flex-col items-end">
                <span className="text-sm text-white font-bold">
                  {user.name}
                </span>
                <span className="text-xs text-gray-400">{user.email}</span>
              </div>

              {/* Role Badge */}
              <Badge
                variant={user.user_type === "investor" ? "info" : "success"}
                size="sm"
              >
                {user.user_type}
              </Badge>

              {/* Sign Out Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="hidden sm:block"
              >
                Sign Out
              </Button>

              {/* Mobile Sign Out - Just Icon */}
              <button
                onClick={handleSignOut}
                className="sm:hidden text-white hover:text-gray-400 transition-colors p-2"
                aria-label="Sign out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  }
);

Navbar.displayName = "Navbar";

export default Navbar;
